import {
  AlertTriangle,
  Loader2,
  Mail,
  User,
  UserPlus,
  Users,
} from "lucide-react";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-hot-toast";
import { Button } from "../../../components/ui/Button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../../components/ui/Card";
import { Input } from "../../../components/ui/Input";
import { Skeleton } from "../../../components/ui/Skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../../components/ui/Table";
import { fetchWithAuth } from "../../../hooks/useAuth";
import { SesiuniAdminCard } from "../SesiuniAdminCard";



interface AdminUser {
  id: number;
  email: string;
  name: string | null;
  lastLoginAt: string | null;
  createdAt: string;
}

export const UserManagementTab = ({ selectedTab }: { selectedTab: string }) => {
  const queryClient = useQueryClient();

  // Fetch admin users
  const {
    data: adminUsers,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey: ["admin-users"],
    queryFn: async () => {
      const response = await fetchWithAuth("/api/admin/settings/users");
      if (!response.ok) throw new Error("Eroare la încărcarea utilizatorilor");
      return response.json() as Promise<AdminUser[]>;
    },
    enabled: selectedTab === "users",
  });
  // Create admin user mutation
  const createAdminUser = useMutation({
    mutationFn: async (data: { email: string; name?: string }) => {
      const response = await fetchWithAuth("/api/admin/settings/users", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });
      if (!response.ok) throw new Error("Eroare la crearea utilizatorului");
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-users"] });
      toast.success("Utilizator creat cu succes");
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });

  if (isLoading) {
    return (
      <>
        <Card className="">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="w-5 h-5" />
              Utilizatori Admin
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Email</TableHead>
                  <TableHead>Nume</TableHead>
                  <TableHead>Ultima Autentificare</TableHead>
                  <TableHead>Data Creării</TableHead>
                  <TableHead>Acțiuni</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {Array.from({ length: 5 }).map((_, idx) => (
                  <TableRow key={idx}>
                    <TableCell>
                      <Skeleton className="h-4 w-48" />
                    </TableCell>
                    <TableCell>
                      <Skeleton className="h-4 w-32" />
                    </TableCell>
                    <TableCell>
                      <Skeleton className="h-4 w-36" />
                    </TableCell>
                    <TableCell>
                      <Skeleton className="h-4 w-36" />
                    </TableCell>
                    <TableCell>
                      <Skeleton className="h-8 w-28" />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </>
    );
  }

  if (isError) {
    return (
      <>
        <Card className="">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-500" />
              Eroare la încărcarea utilizatorilor
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div className="text-sm text-muted-foreground">
                {(error as Error)?.message || "A apărut o eroare neașteptată."}
              </div>
              <Button
                variant="secondary"
                onClick={() => refetch()}
                className="ml-4"
              >
                Reîncearcă
              </Button>
            </div>
          </CardContent>
        </Card>
      </>
    );
  }

  if (adminUsers && adminUsers.length === 0) {
    return (
      <>
        <Card className="">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="w-5 h-5" />
              Utilizatori Admin
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-sm text-muted-foreground">
              Nu există utilizatori admin înregistrați încă.
            </div>
          </CardContent>
        </Card>
      </>
    );
  }

  return (
    <>
      <Card className="">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="w-5 h-5" />
            Utilizatori Admin
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Email</TableHead>
                <TableHead>Nume</TableHead>
                <TableHead>Rol</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Acțiuni</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {adminUsers?.map((user) => (
                <TableRow key={user.id}>
                  <TableCell>{user.email}</TableCell>
                  <TableCell>{user.name || "N/A"}</TableCell>
                  <TableCell>{"Admin"}</TableCell>
                  <TableCell>{"Activ"}</TableCell>
                  <TableCell>
                    <Button variant="link">Resetare Parolă</Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <SesiuniAdminCard />

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <UserPlus className="w-5 h-5" />
            Adaugă Utilizator Admin
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form
            className="space-y-4"
            onSubmit={(e) => {
              e.preventDefault();
              const formData = new FormData(e.currentTarget);
              createAdminUser.mutate({
                email: formData.get("email") as string,
                name: formData.get("name") as string,
              });
            }}
          >
            <div>
              <label
                htmlFor="userEmail"
                className="text-sm font-medium text-muted-foreground flex items-center gap-2"
              >
                <Mail className="w-4 h-4" />
                Email
              </label>
              <Input
                id="userEmail"
                name="email"
                type="email"
                required
                className="mt-1"
                placeholder="Introduceți adresa de email"
              />
            </div>
            <div>
              <label
                htmlFor="userName"
                className="text-sm font-medium text-muted-foreground flex items-center gap-2"
              >
                <User className="w-4 h-4" />
                Nume
              </label>
              <Input
                id="userName"
                name="name"
                className="mt-1"
                placeholder="Introduceți numele utilizatorului"
              />
            </div>
            <Button
              type="submit"
              disabled={createAdminUser.isPending}
              className="flex items-center gap-2"
            >
              {createAdminUser.isPending ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Se adaugă...
                </>
              ) : (
                <>
                  <UserPlus className="w-4 h-4" />
                  Adaugă Utilizator
                </>
              )}
            </Button>
          </form>
        </CardContent>
      </Card>
    </>
  );
};
